// src/pages/EditProfile.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Footerbar from "../components/Footerbar"; // Use Footerbar
import Navbar from "../components/Navbar";     // Use Navbar
import CustomAlert from "../components/CustomAlert"; // ADD
import ConfirmationModal from "../components/ConfirmationModal";
import { forgetShowSession } from "../lib/showSession";
import { Camera, Save, AlertCircle, LogOut } from 'lucide-react'; // Import icons

// --- Helper: Inject CSS Keyframes for Spinners ---
if (!document.getElementById('spinner-keyframes')) {
    const keyframes = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    `;
    const styleSheet = document.createElement("style");
    styleSheet.id = 'spinner-keyframes';
    styleSheet.type = "text/css";
    styleSheet.innerText = keyframes;
    document.head.appendChild(styleSheet);
}
// --- End Helper ---


// --- Styles Object DEFINED BEFORE the component ---
//    (with buttonBase defined before dependent styles)
const styles = {
    pageContainer: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: '#12121f', // Dark background
        color: '#f0f0f0',
        overflowX: 'hidden',
        boxSizing: 'border-box',
    },
    contentArea: {
        flexGrow: 1,
        paddingTop: '80px', // Increased space below Navbar for title/avatar
        paddingBottom: '85px', // Increased space above Footerbar
        paddingLeft: '20px',
        paddingRight: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center', // Center content horizontally
        overflowY: 'auto',
        boxSizing: 'border-box',
        width: '100%',
    },
    centeredMessage: { // For loading state
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '40px 20px',
        color: '#a0a0b0',
        flexGrow: 1, // Take up space while loading
    },
    spinner: { // Consistent spinner
        border: '4px solid rgba(255, 255, 255, 0.3)',
        borderLeftColor: '#a0a0ff',
        borderRadius: '50%',
        width: '40px', // Larger spinner for page load
        height: '40px',
        animation: 'spin 1s linear infinite',
        marginBottom: '20px',
    },
    avatarContainer: {
        position: 'relative', // Needed for icon overlay positioning
        cursor: "pointer",
        marginBottom: "25px",
        width: "120px", // Fixed size
        height: "120px",
        borderRadius: "50%",
        overflow: 'hidden', // Hide parts of edit icon that might overflow
        border: '3px solid #a0a0ff', // Use theme color
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.4)',
    },
    avatarImage: {
        display: 'block', // Remove extra space below img
        width: "100%",
        height: "100%",
        objectFit: "cover", // Ensure image covers the circle area
    },
    avatarEditIcon: {
        position: 'absolute',
        bottom: '0',
        left: '0',
        width: '100%',
        height: '35%', // Cover bottom part
        backgroundColor: 'rgba(0, 0, 0, 0.6)', // Semi-transparent overlay
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0.8, // Start slightly transparent
        transition: 'opacity 0.2s ease',
        // ':hover': { opacity: 1 } // Requires CSS modules/libs
    },
    form: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "18px", // Space between form elements
        width: "100%",
        maxWidth: "450px", // Max width for the form itself
    },
    input: {
        width: "100%",
        padding: "14px 18px", // Comfortable padding
        borderRadius: "8px",
        border: "1px solid #4a4a6e", // Darker border
        backgroundColor: "#1a1a2e", // Dark input background
        fontSize: "16px",
        color: "#f0f0f0", // Light text
        textAlign: "left", // Align text left
        outline: "none", // Remove default outline
        boxSizing: 'border-box',
        transition: 'border-color 0.2s ease',
        // ':focus': { borderColor: '#a0a0ff' } // Requires CSS modules/libs
    },
    // --- Button Styles ---
    // Define buttonBase FIRST
    buttonBase: {
        width: "100%",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        padding: "12px",
        borderRadius: "8px",
        fontSize: "16px",
        fontWeight: 'bold',
        border: "none",
        cursor: "pointer",
        transition: 'background-color 0.3s ease, opacity 0.3s ease',
        boxSizing: 'border-box',
    },
    // NOW define buttonPrimary, using buttonBase
    buttonPrimary: (isLoading) => ({
        ...styles.buttonBase, // Inherit base styles NOW SAFE
        backgroundColor: isLoading ? '#5a0ca0' : '#6a0dad', // Darker purple when loading
        color: "white",
        opacity: isLoading ? 0.7 : 1,
        cursor: isLoading ? "not-allowed" : "pointer",
    }),
    // NOW define buttonSecondary, using buttonBase
    buttonSpinner: { // Consistent button spinner
        border: '3px solid rgba(255, 255, 255, 0.3)',
        borderLeftColor: '#fff',
        borderRadius: '50%',
        width: '16px',
        height: '16px',
        animation: 'spin 1s linear infinite',
        display: 'inline-block',
    },
    errorBanner: {
        width: '100%',
        backgroundColor: 'rgba(255, 96, 96, 0.1)',
        border: '1px solid rgba(255, 96, 96, 0.3)',
        color: '#ff8080', // Lighter red for text
        padding: '10px 15px',
        borderRadius: '8px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        textAlign: 'left',
        marginBottom: '5px', // Add some space below if needed
        boxSizing: 'border-box',
    },
    profileGreeting: {
        width: '100%',
        maxWidth: '450px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '20px',
    },
    greetingTitle: {
        margin: 0,
        color: '#f3f4fa',
        fontSize: '20px',
        fontWeight: 700,
        textAlign: 'center',
    },
    logoutButton: {
        width: '100%',
        minHeight: '44px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '9px',
        border: '1px solid #74454f',
        borderRadius: '6px',
        color: '#ffc0c5',
        backgroundColor: '#35232c',
        fontSize: '14px',
        fontWeight: 650,
        cursor: 'pointer',
    },
}; // --- End of Styles Object ---


// --- Component Definition ---
export default function EditProfile() {
    // State for user data from API + form input
    const [user, setUser] = useState({ name: "", email: "", phone: "" });
    // Separate state for the avatar file to be uploaded
    const [avatarFile, setAvatarFile] = useState(null);
    // State for the image preview URL
    const [avatarPreview, setAvatarPreview] = useState("https://via.placeholder.com/120?text=?"); // Initial placeholder
    const [loading, setLoading] = useState(true); // For initial user fetch
    const [isSubmitting, setIsSubmitting] = useState(false); // For form submission
    const [error, setError] = useState(null); // For displaying errors
    const [alertInfo, setAlertInfo] = useState({ show: false, message: "", type: "error" });
    const [confirmLogout, setConfirmLogout] = useState(false);

    const navigate = useNavigate();
    const fileInputRef = useRef(null); // Ref for the hidden file input

    const logout = () => {
        forgetShowSession();
        localStorage.removeItem('token');
        window.location.href = '/';
    };

    // --- Fetch Initial User Data ---
    useEffect(() => {
        let isMounted = true;
        const fetchUser = async () => {
            setLoading(true);
            setError(null);
            console.log("EditProfile: Fetching user data...");
            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    if (isMounted) navigate('/');
                    return;
                }
                const response = await axios.get("https://apem.riderup.uk/api/user", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (isMounted) {
                    const fetchedUser = response.data;
                    console.log("EditProfile: User data received:", fetchedUser);
                    setUser({ // Set only the fields needed for the form
                        name: fetchedUser.name || "",
                        email: fetchedUser.email || "",
                        phone: fetchedUser.phone || "" // Assuming phone comes formatted or needs formatting display logic
                    });
                    // Set preview based on fetched data, provide solid fallback
                    setAvatarPreview(fetchedUser.avatar ? `https://apem.riderup.uk${fetchedUser.avatar}?${Date.now()}` : "https://via.placeholder.com/120?text=User"); // Add cache buster
                    setLoading(false);
                }
            } catch (error) {
                console.error("EditProfile: Erro ao buscar usuário:", error);
                if (isMounted) {
                    if (axios.isAxiosError(error) && error.response?.status === 401) {
                        localStorage.removeItem("token");
                        navigate('/'); // Redirect on 401
                    } else {
                        setError("Não foi possível carregar seus dados. Tente novamente.");
                        setLoading(false);
                    }
                }
            }
        };
        fetchUser();
        return () => { isMounted = false; }; // Cleanup
    }, [navigate]);

    // --- Form Input Handlers ---
    const handleChange = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value });
    };

    // Phone Masking
    const handlePhoneChange = (e) => {
        let value = e.target.value.replace(/\D/g, ""); // Remove non-digits

        if (value.length > 11) value = value.slice(0, 11); // Limit length

        let formattedValue = "";
        if (value.length > 0) {
            formattedValue = `(${value.slice(0, 2)}`;
        }
        if (value.length >= 3) {
            formattedValue += `) ${value.slice(2, 7)}`;
        }
        if (value.length >= 8) {
            formattedValue += `-${value.slice(7)}`;
        }
        if (value.length === 2 && e.nativeEvent.inputType !== 'deleteContentBackward') {
            formattedValue += ') ';
        } else if (value.length < 2 && formattedValue === '(') {
            // Keep '('
        }

        setUser({ ...user, phone: formattedValue });
    };


    // Avatar Selection
    const handleAvatarChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith("image/")) {
            if (file.size > 5 * 1024 * 1024) {
                setAlertInfo({ show: true, message: "Erro: A imagem não pode ser maior que 5MB.", type: 'error' });
                return;
            }
            setAvatarFile(file);
            setAvatarPreview(URL.createObjectURL(file));
            setError(null);
        } else if (file) {
            setAlertInfo({ show: true, message: "Erro: Por favor, selecione um arquivo de imagem válido (JPG, PNG, GIF, etc.).", type: 'error' });
            if(fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    // Trigger hidden file input
    const handleAvatarClick = () => {
        fileInputRef.current?.click();
    };

    // --- Form Submission ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        setIsSubmitting(true);
        setError(null);
        console.log("EditProfile: Submitting profile update...");

        if (!user.name.trim() || !user.email.trim()) {
            setError("Nome e Email são obrigatórios.");
            setIsSubmitting(false);
            return;
        }
        if (!/\S+@\S+\.\S+/.test(user.email)) {
            setError("Formato de email inválido.");
            setIsSubmitting(false);
            return;
        }
        const justDigitsPhone = user.phone.replace(/\D/g, "");
        if (justDigitsPhone.length < 10 || justDigitsPhone.length > 11) {
            setError("Número de telefone inválido (deve ter 10 ou 11 dígitos).");
            setIsSubmitting(false);
            return;
        }

        const formData = new FormData();
        formData.append("name", user.name.trim());
        formData.append("email", user.email.trim());
        formData.append("phone", justDigitsPhone);
        if (avatarFile) {
            formData.append("avatar", avatarFile, avatarFile.name);
            console.log("EditProfile: Appending new avatar file:", avatarFile.name);
        } else {
            console.log("EditProfile: No new avatar file selected.");
        }

        try {
            const token = localStorage.getItem("token");
            if (!token) throw new Error("Token de autenticação não encontrado.");

            console.log("EditProfile: Sending update request...");
            const response = await axios.post("https://apem.riderup.uk/api/updateProfile", formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                },
            });

            console.log("EditProfile: Update successful:", response.data);
            setAlertInfo({ show: true, message: "Perfil atualizado com sucesso!", type: 'success' });
            if (response.data?.avatar) {
                // Add cache buster to ensure refresh
                setAvatarPreview(`https://apem.riderup.uk${response.data.avatar}?${Date.now()}`);
            }
            setAvatarFile(null);

        } catch (error) {
            console.error("EditProfile: Erro ao atualizar perfil:", error);
            let errorMessage = "Erro ao atualizar perfil.";
            if (axios.isAxiosError(error) && error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
                if (error.response.status === 401) {
                    localStorage.removeItem("token");
                    navigate('/');
                    return;
                }
            } else if (error instanceof Error) {
                errorMessage = error.message;
            }
            setError(errorMessage);
            // alert(errorMessage); // Error is shown in banner now

        } finally {
            if (document.getElementById('edit-profile-root')) {
                setIsSubmitting(false);
            }
        }
    };

    const handleCloseAlert = () => setAlertInfo({ ...alertInfo, show: false });

    // --- Render ---
    return (
        <div id="edit-profile-root" style={styles.pageContainer}>
            {alertInfo.show && <CustomAlert message={alertInfo.message} type={alertInfo.type} onclose={handleCloseAlert} />}
            <ConfirmationModal show={confirmLogout} message="Deseja sair do aplicativo?" onConfirm={logout} onCancel={() => setConfirmLogout(false)} />
            <Navbar title="Editar Perfil" showLogo={false} />

            <div style={styles.contentArea}>
                {loading ? (
                    <div style={styles.centeredMessage}>
                        <div style={styles.spinner}></div>
                        <p>Carregando seus dados...</p>
                    </div>
                ) : (
                    <>
                        <div onClick={handleAvatarClick} style={styles.avatarContainer} title="Clique para alterar avatar">
                            <img
                                src={avatarPreview}
                                alt="Avatar Preview"
                                style={styles.avatarImage}
                                onError={(e) => { e.target.src = 'https://via.placeholder.com/120?text=Error'; }}
                            />
                            <div style={styles.avatarEditIcon}>
                                <Camera size={20} color="#fff" />
                            </div>
                        </div>
                        <div style={styles.profileGreeting}>
                            <h2 style={styles.greetingTitle}>Olá, {user.name}</h2>
                            <button type="button" style={styles.logoutButton} onClick={() => setConfirmLogout(true)}>
                                <LogOut size={18} />Sair do aplicativo
                            </button>
                        </div>
                        <input
                            id="avatarInput"
                            ref={fileInputRef}
                            type="file"
                            accept="image/png, image/jpeg, image/gif, image/webp"
                            onChange={handleAvatarChange}
                            style={{ display: "none" }}
                            aria-hidden="true"
                        />

                        <form onSubmit={handleSubmit} style={styles.form}>
                            {error && (
                                <div style={styles.errorBanner}>
                                    <AlertCircle size={18} style={{ marginRight: '8px', flexShrink: 0 }}/>
                                    <span>{error}</span>
                                </div>
                            )}

                            <input
                                type="text"
                                name="name"
                                placeholder="Nome Completo"
                                value={user.name}
                                onChange={handleChange}
                                required
                                style={styles.input}
                                aria-label="Nome Completo"
                                disabled={isSubmitting}
                            />
                            <input
                                type="email"
                                name="email"
                                placeholder="Seu melhor Email"
                                value={user.email}
                                onChange={handleChange}
                                required
                                style={styles.input}
                                aria-label="Email"
                                disabled={isSubmitting}
                            />
                            <input
                                type="tel"
                                name="phone"
                                placeholder="(XX) XXXXX-XXXX"
                                value={user.phone}
                                onChange={handlePhoneChange}
                                required
                                maxLength={15}
                                style={styles.input}
                                aria-label="Telefone"
                                inputMode="tel"
                                disabled={isSubmitting}
                            />
                            <button
                                type="submit"
                                style={styles.buttonPrimary(isSubmitting)}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div style={styles.buttonSpinner}></div> Salvando...
                                    </>
                                ) : (
                                    <>
                                        <Save size={18} /> Salvar Alterações
                                    </>
                                )}
                            </button>
                        </form>
                    </>
                )}
            </div> {/* End Content Area */}

            <Footerbar />
        </div> // End Page Container
    );
}
